const API_BASE = process.env.REACT_APP_API_URL || '/api';

function getAuthHeaders() {
  const token = sessionStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ---- In-memory GET cache (per browser tab) ----
// Pages unmount on navigation and refetch on every visit, which made each
// click on Dashboard / Case Management / Batch Management pay a full backend
// round-trip. Hot GETs are cached here so repeat visits render instantly.
// Correctness: every admin mutation invalidates the affected keys (so your
// own changes always show immediately), and the TTL is only a fallback for
// data changed outside this tab (VA submissions arriving, grading
// completing). The whole cache is cleared on login/logout.
const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes
const _cache = new Map();

function cacheGet(key) {
  const e = _cache.get(key);
  return e && (Date.now() - e.ts) < CACHE_TTL_MS ? e.data : null;
}

function cacheSet(key, data) {
  _cache.set(key, { data, ts: Date.now() });
}

function invalidate(...prefixes) {
  for (const k of Array.from(_cache.keys())) {
    if (prefixes.some((p) => k.startsWith(p))) _cache.delete(k);
  }
}

// ---- Per-batch trainee-results navigation snapshot ----
// NOT a TTL cache: per-batch grading data deliberately stays uncached (see
// above). This holds only the most recently loaded Batch Results payload so
// "Back to All Trainees" from the VA Evaluation Overview restores the table
// instantly instead of re-running the whole-batch fetch (~15s on a large
// batch). Freshness is preserved two ways: (a) every score override records
// its submission_id here, and the restoring page re-reads those rows fresh
// (millisecond point-reads) and patches them in; (b) any mutation that can
// change rows beyond a single patch (delete / mark-NS / re-queue) drops the
// snapshot entirely, forcing a full refetch.
let _batchSnapshot = null; // { batch, data }
let _snapshotStaleIds = new Set();

export function saveBatchSnapshot(batch, data) {
  _batchSnapshot = { batch: String(batch), data };
  _snapshotStaleIds = new Set();
}

export function takeBatchSnapshot(batch) {
  return (_batchSnapshot && _batchSnapshot.batch === String(batch)) ? _batchSnapshot.data : null;
}

export function getSnapshotStaleIds() {
  return Array.from(_snapshotStaleIds);
}

export function clearBatchSnapshot() {
  _batchSnapshot = null;
  _snapshotStaleIds = new Set();
}

// Sticky trainee-table controls (TS filter, Day filter, search, sort) for
// Batch Results. The page is unmounted by App.js state navigation whenever a
// trainer opens the VA Evaluation Overview, so without this the TS dropdown
// (etc.) silently reset to defaults on "Back to All Trainees" (Gregg,
// 2026-09-01). Kept SEPARATE from the batch snapshot on purpose: mutations
// that drop the snapshot (delete / mark-NS / re-queue) force a data refetch
// but should NOT discard the user's filter choice - only login/logout clears
// the controls.
let _batchViewControls = null; // { batch, controls }

export function saveBatchViewControls(batch, controls) {
  _batchViewControls = { batch: String(batch), controls };
}

export function takeBatchViewControls(batch) {
  return (_batchViewControls && _batchViewControls.batch === String(batch))
    ? _batchViewControls.controls : null;
}

export function clearBatchViewControls() {
  _batchViewControls = null;
}

export async function adminLogin(email, password) {
  const res = await fetch(`${API_BASE}/portal-admin/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const data = await res.json();
  if (data.token) {
    sessionStorage.setItem('admin_token', data.token);
    sessionStorage.setItem('admin_session', JSON.stringify(data));
    _cache.clear();
    clearBatchSnapshot();
    clearBatchViewControls();
  }
  return data;
}

export async function getScenarios() {
  const cached = cacheGet('scenarios');
  if (cached) return cached;
  const res = await fetch(`${API_BASE}/scenarios`, {
    headers: getAuthHeaders(),
  });
  const data = await res.json();
  if (!data.error) cacheSet('scenarios', data);
  return data;
}

export async function getUploadAudit(scenarioId) {
  const qs = scenarioId ? `?scenario_id=${encodeURIComponent(scenarioId)}` : '';
  const res = await fetch(`${API_BASE}/scenarios/upload-audit${qs}`, {
    headers: getAuthHeaders(),
  });
  return res.json();
}

export async function getOverrideAnalytics(batch) {
  const res = await fetch(`${API_BASE}/override-analytics?batch=${encodeURIComponent(batch)}`, {
    headers: getAuthHeaders(),
  });
  return res.json();
}

export async function getCostBenefit() {
  const res = await fetch(`${API_BASE}/cost-benefit`, {
    headers: getAuthHeaders(),
  });
  return res.json();
}

export async function createScenario(name, suspenseDate) {
  const res = await fetch(`${API_BASE}/scenarios/manage`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ name, suspense_date: suspenseDate }),
  });
  invalidate('scenarios', 'dashboard');
  return res.json();
}

export async function updateScenario(id, updates) {
  const res = await fetch(`${API_BASE}/scenarios/manage`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ id, updates }),
  });
  invalidate('scenarios', 'dashboard');
  return res.json();
}

export async function deleteScenario(id) {
  const res = await fetch(`${API_BASE}/scenarios/manage`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ id }),
  });
  invalidate('scenarios', 'dashboard');
  return res.json();
}

export async function uploadGradingSheet(scenarioId, file) {
  const res = await fetch(`${API_BASE}/scenarios/grading-sheet?scenario_id=${scenarioId}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: file,
  });
  invalidate('scenarios', 'dashboard');
  return res.json();
}

export async function uploadTranscript(scenarioId, file) {
  const res = await fetch(`${API_BASE}/scenarios/transcript?scenario_id=${scenarioId}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: file,
  });
  invalidate('scenarios', 'dashboard');
  return res.json();
}

export async function getBatchResults(batch) {
  if (!batch) {
    const cached = cacheGet('batchlist');
    if (cached) return cached;
  }
  const url = batch ? `${API_BASE}/batches/results?batch=${batch}` : `${API_BASE}/batches/results`;
  const res = await fetch(url, {
    headers: getAuthHeaders(),
  });
  const data = await res.json();
  if (!batch && !data.error) cacheSet('batchlist', data);
  return data;
}

// Fresh per-VA results (server-side filtered to one VA's rows). Used by the
// VA Evaluation Overview to refresh itself - deliberately NOT cached, this is
// live grading data (override results must appear immediately).
// cId / vaEmail are the stable identifiers (Monday #12017317679). They are
// OR'd with the name server-side, so a trainee renamed mid-batch still gets
// their whole history instead of only the rows under the requested spelling.
export async function getVaResults(batch, vaName, cId = '', vaEmail = '') {
  const params = new URLSearchParams({ batch, va_name: vaName });
  if (cId) params.set('c_id', cId);
  if (vaEmail) params.set('va_email', vaEmail);
  const res = await fetch(`${API_BASE}/batches/results?${params.toString()}`, {
    headers: getAuthHeaders(),
  });
  return res.json();
}

export async function getSubmissionDetail(submissionId) {
  const res = await fetch(`${API_BASE}/submissions/detail?submission_id=${submissionId}`, {
    headers: getAuthHeaders(),
  });
  return res.json();
}

export async function deleteSubmission(submissionId) {
  const res = await fetch(`${API_BASE}/submissions/detail?submission_id=${submissionId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  invalidate('dashboard', 'batchlist');
  clearBatchSnapshot();
  return res.json();
}

export async function markNotSubmitted({ batch, vaName, vaEmail, scenarioId }) {
  const res = await fetch(`${API_BASE}/submissions/mark-ns`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({
      batch,
      va_name: vaName,
      va_email: vaEmail,
      scenario_id: scenarioId,
    }),
  });
  invalidate('dashboard', 'batchlist');
  clearBatchSnapshot();
  return res.json();
}

export async function requeueSubmission(submissionId) {
  const res = await fetch(`${API_BASE}/submissions/requeue`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ submission_id: submissionId }),
  });
  invalidate('dashboard', 'batchlist');
  clearBatchSnapshot();
  return res.json();
}

export async function overrideScore(submissionId, overrides) {
  const res = await fetch(`${API_BASE}/submissions/override`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ submission_id: submissionId, overrides }),
  });
  invalidate('dashboard', 'batchlist');
  // Mark this row stale in the navigation snapshot so the Batch Results page
  // re-reads it fresh (point-read) when restored - overrides change scores.
  _snapshotStaleIds.add(submissionId);
  return res.json();
}

export function getSubmissionPdfUrl(submissionId) {
  const token = sessionStorage.getItem('admin_token');
  return `${API_BASE}/submissions/pdf?submission_id=${submissionId}&token=${token}`;
}

export async function addTrainerNotes(submissionId, notes) {
  const res = await fetch(`${API_BASE}/submissions/trainer-notes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ submission_id: submissionId, notes }),
  });
  invalidate('dashboard', 'batchlist');
  return res.json();
}

export async function generateFeedbackSummary(submissionId) {
  // (Re)generates the Overall AI Summary Review from the current stored
  // scores (includes any grader overrides). No cache invalidation needed —
  // the summary never changes scores, averages, or list views.
  const res = await fetch(`${API_BASE}/submissions/feedback-summary`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ submission_id: submissionId }),
  });
  return res.json();
}

export async function excludeFromAverage(submissionId, excluded) {
  const res = await fetch(`${API_BASE}/submissions/exclude-average`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ submission_id: submissionId, excluded }),
  });
  invalidate('dashboard', 'batchlist');
  // Averages change: mark the row stale so a restored Batch Results snapshot
  // re-reads it and recomputes the VA's average.
  _snapshotStaleIds.add(submissionId);
  return res.json();
}

// --- Summary Download ---

export function getSummaryDownloadUrl(batch, vaName, cId = '', vaEmail = '') {
  const token = sessionStorage.getItem('admin_token');
  const params = new URLSearchParams({ batch, va_name: vaName });
  // Stable identifiers so a renamed trainee downloads ONE complete mistakes
  // CSV rather than the half filed under this spelling (Monday #12017317679).
  if (cId) params.set('c_id', cId);
  if (vaEmail) params.set('va_email', vaEmail);
  params.set('token', token);
  return `${API_BASE}/summary/download?${params.toString()}`;
}

// --- Dashboard Stats ---

export async function getDashboardStats() {
  const cached = cacheGet('dashboard');
  if (cached) return cached;
  const res = await fetch(`${API_BASE}/portal-admin/dashboard`, {
    headers: getAuthHeaders(),
  });
  const data = await res.json();
  if (!data.error) cacheSet('dashboard', data);
  return data;
}

// --- Batch Management ---

export async function getBatches() {
  const cached = cacheGet('batches');
  if (cached) return cached;
  const res = await fetch(`${API_BASE}/batches/manage`, {
    headers: getAuthHeaders(),
  });
  const data = await res.json();
  if (!data.error) cacheSet('batches', data);
  return data;
}

export async function createBatch(data) {
  const res = await fetch(`${API_BASE}/batches/manage`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(data),
  });
  invalidate('batches', 'dashboard', 'batchlist');
  return res.json();
}

export async function updateBatch(data) {
  const res = await fetch(`${API_BASE}/batches/manage`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(data),
  });
  invalidate('batches', 'dashboard', 'batchlist');
  return res.json();
}

export async function archiveBatch(batchNumber) {
  const res = await fetch(`${API_BASE}/batches/archive?batch=${encodeURIComponent(batchNumber)}`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  invalidate('batches', 'dashboard', 'batchlist');
  return res.json();
}

export async function deleteBatch(batchNumber) {
  const res = await fetch(`${API_BASE}/batches/manage`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ batch_number: batchNumber }),
  });
  invalidate('batches', 'dashboard', 'batchlist');
  return res.json();
}

// --- User Management ---

export async function getUsers() {
  const res = await fetch(`${API_BASE}/portal-admin/users`, {
    headers: getAuthHeaders(),
  });
  return res.json();
}

export async function createUserAccount(email, name, role, developer = false) {
  const res = await fetch(`${API_BASE}/portal-admin/users`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ email, name, role, developer }),
  });
  return res.json();
}

export async function updateUserAccount(email, updates) {
  const res = await fetch(`${API_BASE}/portal-admin/users`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ email, updates }),
  });
  return res.json();
}

export async function deleteUserAccount(email) {
  const res = await fetch(`${API_BASE}/portal-admin/users`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ email }),
  });
  return res.json();
}

export async function resetUserPassword(email) {
  const res = await fetch(`${API_BASE}/portal-admin/users/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ email }),
  });
  return res.json();
}

export async function changeMyPassword(email, oldPassword, newPassword) {
  const res = await fetch(`${API_BASE}/portal-admin/change-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, old_password: oldPassword, new_password: newPassword }),
  });
  return res.json();
}

export async function getDevSettings() {
  const res = await fetch(`${API_BASE}/portal-admin/dev-settings`, {
    headers: getAuthHeaders(),
  });
  return res.json();
}

export async function setDevDateOverride(dateStr) {
  const res = await fetch(`${API_BASE}/portal-admin/dev-settings`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ test_date_override: dateStr }),
  });
  return res.json();
}

export async function setTestMode(enabled) {
  const res = await fetch(`${API_BASE}/portal-admin/dev-settings`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ test_mode: enabled }),
  });
  return res.json();
}

export function getSession() {
  const data = sessionStorage.getItem('admin_session');
  return data ? JSON.parse(data) : null;
}

export function updateSession(patch) {
  const current = getSession() || {};
  const next = { ...current, ...patch };
  sessionStorage.setItem('admin_session', JSON.stringify(next));
  return next;
}

export function clearSession() {
  _cache.clear();
  clearBatchSnapshot();
  clearBatchViewControls();
  sessionStorage.removeItem('admin_token');
  sessionStorage.removeItem('admin_session');
}
